import api from './api';

export interface Expense {
  id: string;  // CUID from systems database
  projectId: number;
  project: {
    id: number;
    name: string;
  };
  expenseHeadId: string;  // CUID from systems database
  expenseHead: {
    id: string;  // CUID from systems database
    name: string;
    description?: string;
  };
  expenseDate: string;
  voucherNo?: string;
  narration?: string;
  amount: number;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  updatedBy?: string;
}

export interface ExpenseHead {
  id: string;  // Changed from number to string to match CUID from systems DB
  name: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ExpenseFilters {
  search?: string;
  projectId?: number;
  expenseHeadId?: string;  // Changed from number to string to match CUID from systems DB
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

export interface CreateExpenseEntry {
  expenseHeadId: string;  // Changed from number to string to match CUID from systems DB
  expenseDate: string;
  voucherNo?: string;
  narration?: string;
  amount: number;
}

export interface CreateExpensesRequest {
  projectId: number;
  expenses: CreateExpenseEntry[];
}

class ExpenseService {
  // Helper to get headers with hidden mode flag
  private getHiddenModeHeaders() {
    return {
      'x-hidden-mode': 'true'
    };
  }

  // Expense Head methods
  async getExpenseHeads(): Promise<ExpenseHead[]> {
    const response = await api.get(
      '/systems/expense-heads',
      { headers: this.getHiddenModeHeaders() }
    );
    return response.data.data;
  }

  async createExpenseHead(data: { name: string; description?: string }): Promise<ExpenseHead> {
    const response = await api.post(
      '/systems/expense-heads', 
      data,
      { headers: this.getHiddenModeHeaders() }
    );
    return response.data.data;
  }

  // Expense methods
  async getExpenses(filters: ExpenseFilters = {}): Promise<{
    data: Expense[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalRecords: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
  }> {
    const params = new URLSearchParams();
    
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.search) params.append('search', filters.search);
    if (filters.projectId) params.append('projectId', filters.projectId.toString());
    if (filters.expenseHeadId) params.append('expenseHeadId', filters.expenseHeadId.toString());
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);

    const response = await api.get(
      `/systems/expenses?${params}`,
      { headers: this.getHiddenModeHeaders() }
    );
    
    return {
      data: response.data.data,
      pagination: {
        currentPage: response.data.pagination.page,
        totalPages: response.data.pagination.totalPages,
        totalRecords: response.data.pagination.total,
        hasNext: response.data.pagination.page < response.data.pagination.totalPages,
        hasPrev: response.data.pagination.page > 1
      }
    };
  }

  async getExpenseById(id: string): Promise<Expense> {  // Changed from number to string
    const response = await api.get(`/systems/expenses/${id}`, {
      headers: {
        'x-hidden-mode': 'true'  // Required for systems data
      }
    });
    return response.data.data;
  }

  async createExpenses(data: CreateExpensesRequest): Promise<Expense[]> {
    const response = await api.post(
      '/systems/expenses', 
      data,
      { headers: this.getHiddenModeHeaders() }
    );
    return response.data.data;
  }

  async updateExpense(id: string, data: Partial<CreateExpenseEntry>): Promise<Expense> {  // Changed from number to string
    const response = await api.put(`/systems/expenses/${id}`, data, {
      headers: this.getHiddenModeHeaders()
    });
    return response.data.data;
  }

  async deleteExpense(id: string): Promise<void> {  // Changed from number to string
    await api.delete(`/systems/expenses/${id}`, {
      headers: this.getHiddenModeHeaders()
    });
  }

  // Utility functions
  formatAmount(amount: number): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  validateExpenseData(data: CreateExpenseEntry): string[] {
    const errors: string[] = [];

    if (!data.expenseHeadId || data.expenseHeadId.trim() === '') {
      errors.push('Expense head is required');
    }

    if (!data.expenseDate) {
      errors.push('Expense date is required');
    }

    if (!data.amount || data.amount <= 0) {
      errors.push('Amount must be greater than 0');
    }

    return errors;
  }

  validateBulkExpenseData(expenses: CreateExpenseEntry[]): string[] {
    const errors: string[] = [];

    if (!expenses || expenses.length === 0) {
      errors.push('At least one expense entry is required');
    }

    expenses.forEach((expense, index) => {
      const expenseErrors = this.validateExpenseData(expense);
      if (expenseErrors.length > 0) {
        errors.push(`Row ${index + 1}: ${expenseErrors.join(', ')}`);
      }
    });

    return errors;
  }

  // Helper to create empty expense row
  createEmptyExpenseRow(): CreateExpenseEntry {
    return {
      expenseHeadId: '',  // Changed from 0 to empty string to match CUID format
      expenseDate: new Date().toISOString().split('T')[0],
      voucherNo: '',
      narration: '',
      amount: 0
    };
  }

  // Calculate total amount for multiple expenses
  calculateTotal(expenses: CreateExpenseEntry[]): number {
    return expenses.reduce((total, expense) => total + (expense.amount || 0), 0);
  }

  // Check if expense data is complete
  isExpenseComplete(expense: CreateExpenseEntry): boolean {
    return !!(expense.expenseHeadId && expense.expenseHeadId.trim() && expense.expenseDate && expense.amount > 0);
  }

  // Get incomplete expense rows
  getIncompleteRows(expenses: CreateExpenseEntry[]): number[] {
    return expenses
      .map((expense, index) => ({ expense, index }))
      .filter(({ expense }) => !this.isExpenseComplete(expense))
      .map(({ index }) => index);
  }

  // Upload expenses from file
  async uploadExpenseData(file: File, projectId: number): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('projectId', projectId.toString());

    const response = await api.post('/upload-data/expense', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'x-hidden-mode': 'true'  // Required for expense upload
      }
    });
    
    return response.data;
  }
}

const expenseService = new ExpenseService();
export { expenseService };
export default expenseService;
